<template lang="pug">
    .form(v-if="isAccessToForm")
        .form__container
            .form__title
                | {{ pageTitle }}
            form.form__body(
                action="javascript://"
                @submit="submitForm"
                v-if="!isLoading"
            )
                .form__field
                    .form__label
                        | {{ $i18n.t('school.school_form_name') }}
                    .form__field-body
                        cs-input.form__input(
                            :error="errors.has('name')"
                            type="text"
                            v-model="subscription.name"
                            name="name"
                            v-validate="'required'"
                            :data-vv-as="$i18n.t('billing.billing_subscriptions_name')"
                        )
                        help-text(v-if="errors.has('name')" error)
                            | {{ errors.first('name')}}
                .form__field.fields__element_date
                    .form__label
                        | {{ $i18n.t('fields.fields_period') }}
                    .form__field-body
                        .fields__element--period
                            cs-datepicker.form__input.datepicker-input-container--no-margin(
                                :language='user.locale',
                                :value='subscription.start',
                                :format="customFormatter",
                                @input="subscription.start = dateToMoment($event, 'YYYY-MM-DD')",
                                v-validate='`required|date_format:YYYY-MM-DD`',
                                :data-vv-as="$i18n.t('billing.billing_stocks_startDate').toLowerCase()",
                                name='startDate',
                                :disabledDates="disableAllDaysExceptFirst()"
                                )
                            div.fields__divider —
                            cs-datepicker.form__input.datepicker-input-container--no-margin(
                                :language='user.locale',
                                :value='subscription.end',
                                :format="customFormatter",
                                @input="subscription.end = dateToMoment($event, 'YYYY-MM-DD')",
                                v-validate='`required|date_format:YYYY-MM-DD|after:${subscription.start}`',
                                :data-vv-as="$i18n.t('billing.billing_stocks_endDate').toLowerCase()",
                                name='endDate'
                                :disabledDates="disableAllDaysExceptLast()"
                            )
                        help-text(slot='help-text', v-if="errors.has('startDate')", error)
                            | {{ errors.first('startDate') }}
                        help-text(slot='help-text', v-if="errors.has('endDate')", error)
                            | {{ errors.first('endDate') }}
                divider
                .form__field
                    .form__label
                        | {{ $i18n.t('billing.billing_stocks_description') }}
                    .form__field-body
                        textarea.form__textarea(
                            :class="{'is-danger': errors.has('description')}"
                            v-model="subscription.description"
                            name="description"
                            v-validate="'max:30'"
                            :data-vv-as="$i18n.t('billing.billing_stocks_description').toLowerCase()"
                        )
                        help-text(slot='help-text', v-show="errors.has('description')", error)
                            | {{ errors.first('description') }}
                .form__field
                    .form__label
                    .form__field-body
                        help-text(
                            slot='help-text',
                            v-if="subscription && subscription.start && subscription.end"
                            v-text=" $i18n.t('billing.billing_subscriptions_dateHelper', {\
                               from: subscription.start + ' 00:00:00',\
                               to: subscription.end + ' 00:00:00'\
                            })"
                        )
                divider
                .form__field
                    .form__label.form__label--bold
                        | {{ $i18n.t('billing.billing_subscriptions_amountOfStudents') }}
                    .form__field-body
                template(v-for='course in courseTypes')
                    .form__field
                        .form__label
                            | {{ $i18n.t('common.common_course_' + course) }}
                        .form__field-body
                            cs-input.input-number.input-number--small(
                                type='number',
                                :value='subscription[camelCase(course + \'Students\')]',
                                v-model="subscription[camelCase(course + \'Students\')]",
                                :name='camelCase(course + \'Students\')',
                            )
                divider
                .form__field
                    .form__label
                        | {{ $i18n.t('billing.billing_settings_schools') }}
                    .form__field-body
                        cs-multiselect(
                            :options="schools",
                            v-model="subscription.schools",
                            :placeholder="schoolsPlaceholder",
                            label="name",
                            trackBy="id",
                            :loading="loadingSchools",
                            @open="getSchools",
                            :hideSelected="true",
                            :showNoResults="false",
                            :closeOnSelect="false",
                            :searchable="true",
                            :disabled="!!notIncludedSchools.length",
                        )
                divider
                .form__field
                    .form__label
                        | {{ $i18n.t('billing.billing_settings_cost') }}
                    .form__field-body
                        cs-input.input-number.input-number--small(
                            type='number',
                            :value='subscription.cost',
                            v-model="subscription.cost"
                        )
                divider
                .form__buttons
                    cs-button.form__button(
                        color="blue"
                        type="submit"
                        :loading="isLoading"
                        @click="submitForm"
                    )
                        | {{ $i18n.t('homework.homework_create_save') }}
                    cs-button.form__button(
                        color="blue"
                        pattern="primary-outline"
                        @click.prevent="$router.back()"
                    )
                        | {{ $i18n.t('common.common_button_cancel') }}
            cs-loader(v-else)
    div(v-else)
        .content-box.list
            .box-body
                .column.is-12-mobile.list-item.field-column
                    | {{ $i18n.t('errors.page_not_access') }}
</template>

<script>
    import { mapState } from 'vuex';
    import moment from 'moment';
    import SchoolApi from '../../../api/school';
    import { camelCase, cloneDeep } from 'lodash';
    import SubscriptionsApi from '../../../api/billing/subscriptions';

    export default {
        props: {
            copy: {
                type: Object,
                default: null,
            },
            type: {
                type: String,
                default: 'subscriptions',
            },
            page: {
                type: [String, Number],
            },
            id: {
                type: [Number, String],
            },
        },
        mounted() {
            this.fetchData();
        },
        data() {
            return {
                subscription: {
                    name: '',
                    schools: [],
                    start: null,
                    end: null,
                    cost: 0,
                    mentalArithmeticStudents: 0,
                    fastReadingStudents: 0,
                    onlineSchoolStudents: 0,
                    mathMagicStudents: 0,
                    memoryDevelopmentStudents: 0,
                    multiplicationStudents: 0,
                    readingEnStudents: 0,
                    russianLanguageStudents: 0,
                    forStudentPayedSchools: false,
                },
                schools: [],
                notIncludedSchools: [],
                loadingSchools: false,
                isLoading: false,
                courseTypes: [
                    'mentalArithmetic',
                    'fastReading',
                    'onlineSchool',
                    'mathMagic',
                    'memoryDevelopment',
                    'multiplication',
                    'readingEn',
                    'russianLanguage',
                ],
            };
        },
        methods: {
            disableAllDaysExceptFirst() {
                return {
                    customPredictor: date => {
                        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
                        return date.getDate() !== firstDay.getDate();
                    },
                };
            },
            disableAllDaysExceptLast() {
                return {
                    customPredictor: date => {
                        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                        return date.getDate() !== lastDay.getDate();
                    },
                };
            },
            fetchData() {
                if (this.copy || this.id) {
                    let id = this.copy?.id || this.id;
                    SubscriptionsApi.show(id)
                        .then(stock => {
                            this.subscription = stock;
                            this.subscription.end = this.dateToMoment(this.subscription.end, 'YYYY-MM-DD');
                            this.subscription.start = this.dateToMoment(this.subscription.start, 'YYYY-MM-DD');
                        })
                        .catch(err => this.$toasted(err));
                }
            },
            showErrorMessage(err) {
                this.isLoading = false;
                if (err.data) {
                    this.$toasted.error(err.message);
                }
            },
            getSchools() {
                if (this.schools.length) {
                    return;
                }
                this.loadingSchools = true;
                SchoolApi.list({
                    all: true,
                    studentPayed: Number(!!this.subscription.forStudentPayedSchools),
                    isBilling: true,
                })
                    .then(data => {
                        let schools = data.schools;
                        this.schools = schools ? schools : [];
                        this.loadingSchools = false;
                    })
                    .catch(() => {
                        this.loadingSchools = false;
                        this.$toasted.error(this.$root.$t('errors.error_has_occurred'));
                    });
            },
            submitForm() {
                this.$validator.validateAll().then(isValid => {
                    if (isValid) {
                        if (this.notIncludedSchools.length === this.subscription.schools.length) {
                            this.$toasted.error(this.$i18n.t('billing.billing_subscriptions_schoolsError'));
                            return;
                        }
                        this.isLoading = true;
                        let subscriptionsCopy = cloneDeep(this.subscription);
                        if (this.copy && !this.id) {
                            delete subscriptionsCopy.id;
                            delete subscriptionsCopy.updatedAt;
                            delete subscriptionsCopy.createdAt;
                        }
                        let params = +this.id ? { id: +this.id, subscription: subscriptionsCopy } : subscriptionsCopy;
                        SubscriptionsApi[this.id ? 'update' : 'create'](params)
                            .then(() => {
                                this.$router.push({ name: 'subscriptions:list' });
                            })
                            .catch(err => this.showErrorMessage(err));
                    }
                });
            },
            dateToMoment(date, format) {
                return moment(date).format(format);
            },
            customFormatter(date) {
                return moment(date).format('D MMM YYYY');
            },
            camelCase,
        },
        computed: {
            ...mapState({
                user: state => state.auth.user,
            }),
            schoolsPlaceholder() {
                if (this.subscription.schools.length) {
                    return this.$i18n.t('common.common_placeholder_enterToSearch');
                } else if (this.notIncludedSchools.length) {
                    return this.$i18n.t('fields.fields_chooseSchoolPlaceholder');
                }
                return this.$i18n.t('olympiads.olympiads_olympiadForm_allSchools');
            },
            notIncludedSchoolsPlaceholder() {
                if (this.notIncludedSchools.length) {
                    return this.$i18n.t('common.common_placeholder_enterToSearch');
                }
                return this.$i18n.t('fields.fields_chooseSchoolPlaceholder');
            },
            pageTitle() {
                return !this.id
                    ? this.$i18n.t('billing.billing_subscriptions_createTitle')
                    : this.$i18n.t('billing.billing_subscriptions_updateTitle');
            },
            isAccessToForm() {
                return this.user.isSuperadmin || this.user.isManagerOrSupport;
            },
        },
    };
</script>

<style lang="scss" scoped>
    .input-number {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        -moz-appearance: textfield;
    }

    .input-number--small {
        max-width: 160px;
    }

    .forks-table {
        display: block;
        overflow-x: auto;
        //align-self: center;
        border-collapse: separate;
        border-spacing: 0;
        width: auto;
        &::-webkit-scrollbar-track,
        &::-webkit-scrollbar-track {
            background: #f1f1f1;
        }
        &::-webkit-scrollbar,
        &::-webkit-scrollbar {
            background-color: #f5f5f5;
            height: 2px;
        }
        &::-webkit-scrollbar-thumb,
        &::-webkit-scrollbar-thumb {
            height: 2px;
            background: $color-grey-600;
        }
    }

    .forks-table__row {
        height: 45px;
    }

    .forks-table__thead {
        font-size: 14px;
        line-height: 20px;
    }

    .forks-table__tbody {
        font-size: 16px;
        line-height: 19px;
    }

    .forks-table__column {
        font-weight: normal;
        min-width: 129px;
        text-align: right;
        padding: 4px;
        vertical-align: middle;
    }

    .forks-table__column--small {
        min-width: 100px;
        max-width: 120px;
    }

    .forks-table__column--x-small {
        min-width: 80px;
    }

    .forks-table__column--separate {
        text-align: right;
        padding-right: 16px;
    }

    .forks-table__column--last {
        min-width: 0;
        width: 50px;
        text-align: right;
    }

    .forks-table__column--last .cs-button--narrow {
        height: 45px;
        padding: 12px 15px;
    }

    .forks-table__column--last .cs-button--disabled {
        border: 1px solid #bdbdbd;
        background-color: transparent;
    }

    .forks-table__column--bold {
        font-weight: 500;
    }

    .forks-table__fork-preview {
        font-weight: normal;
        font-size: 16px;
        line-height: 19px;
    }

    .forks-table__fork-value {
        font-weight: 500;
    }

    .table-responsive-container {
        display: grid;
    }

    .table-responsive {
        overflow-x: auto;
    }
</style>

<style lang="scss" scoped>
    @import '@/assets/sass/forms/form.scss';

    .form__textarea {
        box-sizing: border-box;

        outline: none;

        color: $x-color-black;

        width: 100%;

        border-radius: $border-radius-5;
        border: 1px solid $hsl-color-gray-74;

        font-weight: normal;
        font-size: $font-size-16;
        line-height: $font-size-19;

        //@include inset-squish-space($space-small);

        padding: 12px 15px;

        @include stack-space($space-x-small);

        &:hover {
            border-color: $hsl-color-blue-56;
        }

        &:focus {
            border-color: $hsl-color-blue-56;
        }

        &:active {
            border-color: $hsl-color-blue-56;
        }

        &::placeholder {
            font-weight: lighter;
        }

        align-items: center;
        height: 2.25em;
        justify-content: flex-start;
        position: relative;
        vertical-align: top;
        background-color: #fff;
        box-shadow: inset 0 1px 2px hsla(0, 0%, 4%, 0.1);
        max-width: 100%;

        display: block;
        min-width: 100%;

        word-wrap: break-word;
        resize: none;
        max-height: 200px;
        min-height: 180px;

        overflow: hidden;
        overflow-wrap: break-word;
    }

    .fields__element_date {
        @include media($xlg) {
            display: flex;
            align-items: baseline;
        }
    }

    .fields__element--period {
        display: flex;
        flex-direction: row;
        align-items: center;
        @include stack-space($space-base);
        @include media($xlg) {
            padding: 0;
        }
    }

    .fields__divider {
        text-align: center;
        margin: 0 $space-x-small;
    }
</style>
